import React, { useEffect, useState } from 'react';
import { toLocalDateTimeInput } from '../utils/format';

// Fallbacks — the live lists come from Settings → Configurable Support Lists.
const FALLBACK_ENTRY_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'site_visit', label: 'Site Visit' },
  { value: 'demo', label: 'Demo' },
  { value: 'note', label: 'Note' },
  { value: 'quote_sent', label: 'Quote Sent' },
  { value: 'account_review', label: 'Account Review' },
  { value: 'support_job', label: 'Support Job' },
  { value: 'task', label: 'Task' }
];

const FALLBACK_OUTCOMES = [
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
  { value: 'no_response', label: 'No Response' }
];

const humanise = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

// The entry being edited may carry a system-generated or deactivated value —
// keep it selectable rather than silently rewriting the entry's type.
const withCurrentValue = (options, current) => (
  current && !options.some(option => option.value === current)
    ? [...options, { value: current, label: humanise(current) }]
    : options
);

const toDateTimeLocal = (value) => toLocalDateTimeInput(value || undefined) || toLocalDateTimeInput();

function TimelineEntryEditModal({ entry, authFetch, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [entryTypeOptions, setEntryTypeOptions] = useState(FALLBACK_ENTRY_TYPES);
  const [outcomeOptions, setOutcomeOptions] = useState(FALLBACK_OUTCOMES);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await authFetch('/api/v1/support/config-lists');
        if (!response.ok || cancelled) return;
        const body = await response.json();
        const pick = (key, fallback) => {
          const items = (body.data?.[key] || []).filter(item => item.active !== false);
          return items.length > 0 ? items.map(({ value, label }) => ({ value, label })) : fallback;
        };
        setEntryTypeOptions(pick('commercial_activity_types', FALLBACK_ENTRY_TYPES));
        setOutcomeOptions(pick('commercial_activity_outcomes', FALLBACK_OUTCOMES));
      } catch (err) {
        // Non-fatal: fallbacks stay in place.
      }
    };
    load();
    return () => { cancelled = true; };
  }, [authFetch]);
  const [formData, setFormData] = useState({
    entry_type: entry.entry_type || 'note',
    occurred_at: toDateTimeLocal(entry.occurred_at),
    subject: entry.subject || '',
    body: entry.body || '',
    outcome: entry.outcome || '',
    next_action: entry.next_action || '',
    next_action_date: entry.next_action_date ? entry.next_action_date.split('T')[0] : ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const response = await authFetch(`/api/v1/timeline/${entry.entry_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          entry_type: formData.entry_type,
          occurred_at: new Date(formData.occurred_at).toISOString(),
          subject: formData.subject || null,
          body: formData.body || null,
          outcome: formData.outcome || null,
          next_action: formData.next_action || null,
          next_action_date: formData.next_action_date || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update activity');
      }

      const updated = await response.json();
      onSaved(updated);
    } catch (err) {
      setError(err.message || 'Failed to update activity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Timeline Note</h2>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="modal-error">{error}</div>}

          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit_entry_type">Type *</label>
                <select id="edit_entry_type" name="entry_type" value={formData.entry_type} onChange={handleChange} required>
                  {withCurrentValue(entryTypeOptions, formData.entry_type).map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="edit_occurred_at">Date/Time *</label>
                <input type="datetime-local" id="edit_occurred_at" name="occurred_at" value={formData.occurred_at} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="edit_subject">Subject</label>
              <input id="edit_subject" name="subject" value={formData.subject} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="edit_body">Notes</label>
              <textarea id="edit_body" name="body" value={formData.body} onChange={handleChange} rows={4} />
            </div>

            <div className="form-group">
              <label htmlFor="edit_outcome">Outcome</label>
              <select id="edit_outcome" name="outcome" value={formData.outcome} onChange={handleChange}>
                <option value="">Select outcome...</option>
                {withCurrentValue(outcomeOptions, formData.outcome).map(outcome => (
                  <option key={outcome.value} value={outcome.value}>{outcome.label}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit_next_action">Follow-up Action</label>
                <input id="edit_next_action" name="next_action" value={formData.next_action} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="edit_next_action_date">Follow-up Date</label>
                <input type="date" id="edit_next_action_date" name="next_action_date" value={formData.next_action_date} onChange={handleChange} />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TimelineEntryEditModal;
